import {
  Button,
  ColumnTable,
  DetailBox,
  Modal,
  Table,
  addToast,
} from '@octano/global-ui';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Loading from '../../../../components/info/Loading';
import { useActionKitImp } from '../../../../components/ozmoit/common';
import SectionTitle from '../../../../components/text/SectionTitle';
import { TranslationsKeys } from '../../../../locales/translations';

import { getCourseGroupDetailAction } from '../actions';
import { CourseGroupDetail } from '../types';

interface CourseGroupDetailModalProps {
  isOpen: boolean;
  groupId: number;
  onClose: () => void;
}

interface DetailRow {
  id: number;
  type: 'course' | 'group';
  code: string;
  name: string;
  schoolName: string | null;
  modality: string | null;
  academicLevel: string | null;
  credits: number | null;
}

/**
 * HU 15572 — Modal de detalle (read-only) de una agrupación.
 *
 * - Carga el detalle al abrir vía getCourseGroupDetailAction y lo limpia al
 *   cerrar para que la próxima apertura siempre pida data fresca.
 * - Muestra header con DetailBox (Nombre / Código / Escuela) + tabla mixta:
 *   los cursos exponen modalidad / nivel académico / créditos, mientras
 *   que los sub-grupos muestran "N/A" en esas columnas (resolución
 *   OPEN-2 acordada con producto).
 * - Único botón de acción: Cerrar.
 */
export default function CourseGroupDetailModal({
  isOpen,
  groupId,
  onClose,
}: CourseGroupDetailModalProps): ReactElement {
  const { t } = useTranslation(TranslationsKeys.COURSE_GROUPS);
  const [detail, setDetail] = useState<CourseGroupDetail | null>(null);

  const { action: fetchDetail, loading } = useActionKitImp(
    getCourseGroupDetailAction,
    {
      onSuccess: (resp) => {
        setDetail(resp);
      },
      onError: {
        unknown: () => {
          addToast({
            icon: 'error',
            color: 'danger',
            text: t('errors.generic'),
          });
          onClose();
        },
      },
    },
  );

  // Fetch on open + cleanup on close para que reabrir siempre dispare un
  // refetch (no nos quedamos con la data del groupId anterior).
  useEffect(() => {
    if (isOpen && groupId) {
      setDetail(null);
      fetchDetail(groupId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, groupId]);

  const rows = useMemo<DetailRow[]>(() => {
    if (!detail) {
      return [];
    }
    const courses: DetailRow[] = detail.courses.map((c) => ({
      id: c.id,
      type: 'course',
      code: c.code,
      name: c.name,
      schoolName: detail.school?.name ?? null,
      modality: c.modality,
      academicLevel: c.academicLevel,
      credits: c.credits,
    }));
    const groups: DetailRow[] = detail.childGroups.map((g) => ({
      id: g.id,
      type: 'group',
      code: g.code,
      name: g.name,
      schoolName: g.school?.name ?? null,
      modality: null,
      academicLevel: null,
      credits: null,
    }));
    return [...courses, ...groups];
  }, [detail]);

  const columns = useMemo<ColumnTable<DetailRow>[]>(() => {
    const na = t('items.table.notApplicable');
    return [
      {
        columnName: 'code',
        tdClassName: 'text-center',
        thClassName: 'text-center bg-secondary',
        headerText: t('detailModal.table.code'),
        cellFormat: ({ row }) => row.code,
      },
      {
        columnName: 'name',
        tdClassName: 'text-center',
        thClassName: 'text-center bg-secondary',
        headerText: t('detailModal.table.name'),
        cellFormat: ({ row }) => row.name,
      },
      {
        columnName: 'school',
        tdClassName: 'text-center',
        thClassName: 'text-center bg-secondary',
        headerText: t('detailModal.table.school'),
        cellFormat: ({ row }) => row.schoolName ?? '-',
      },
      {
        columnName: 'modality',
        tdClassName: 'text-center',
        thClassName: 'text-center bg-secondary',
        headerText: t('detailModal.table.modality'),
        cellFormat: ({ row }) => row.modality ?? na,
      },
      {
        columnName: 'academicLevel',
        tdClassName: 'text-center',
        thClassName: 'text-center bg-secondary',
        headerText: t('detailModal.table.academicLevel'),
        cellFormat: ({ row }) => row.academicLevel ?? na,
      },
      {
        columnName: 'credits',
        tdClassName: 'text-center',
        thClassName: 'text-center bg-secondary',
        headerText: t('detailModal.table.credits'),
        cellFormat: ({ row }) =>
          row.credits === null || row.credits === undefined
            ? na
            : String(row.credits),
      },
    ];
  }, [t]);

  if (!isOpen) {
    return <></>;
  }

  return (
    <Modal isOpen={true} toggle={onClose} size="lg" closeOnBackdrop={false}>
      <div>
        <h1 className="text-dark text-center fw-600 fs-20">
          {t('detailModal.title')}
        </h1>
        {loading || !detail ? (
          <Loading insideCard />
        ) : (
          <>
            <div className="mt-3 mb-3">
              <DetailBox
                columns={[
                  {
                    title: t('items.summary.name'),
                    body: detail.name,
                    size: { xs: 12, md: 5 },
                  },
                  {
                    title: t('items.summary.code'),
                    body: detail.code,
                    size: { xs: 12, md: 3 },
                  },
                  {
                    title: t('items.summary.responsibleSchool'),
                    body: detail.school?.name ?? '-',
                    size: { xs: 12, md: 4 },
                  },
                ]}
              />
            </div>
            <SectionTitle text={t('detailModal.section')} />
            <div className="mt-3">
              <Table<DetailRow> data={rows} columns={columns} />
            </div>
          </>
        )}
        <div className="d-flex justify-content-end mt-4">
          <Button
            type="button"
            text={t('detailModal.actions.close')}
            onClick={onClose}
          />
        </div>
      </div>
    </Modal>
  );
}
